import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ESpacing, Tokens } from '@tmov/design-system/theme/types';
import Switch from 'react-switch';
import { Button as AntButton, Input } from 'antd';
import moment from 'moment';

import {
  Button,
  Card,
  Grid,
  Paragraph,
  Tooltip,
  useToast,
} from '@tmov/design-system';
import {
  getDocumentosByProtocolo,
  ativarDocumento,
  atualizarDataValidadeDocumento,
  inativarDocumento,
} from '../../../../../../services/documento';
import * as S from './styles';

const formatarDataValidade = dataValidade => {
  if (!dataValidade || !moment(dataValidade).isValid()) return null;

  return moment(dataValidade).format('YYYY-MM-DD');
};

const formatarDataDigitada = valor => {
  const numeros = valor.replace(/\D/g, '').slice(0, 8);

  if (numeros.length <= 2) return numeros;
  if (numeros.length <= 4) {
    return `${numeros.slice(0, 2)}/${numeros.slice(2)}`;
  }

  return `${numeros.slice(0, 2)}/${numeros.slice(2, 4)}/${numeros.slice(4)}`;
};

export function CardDocumentosConjunto({
  documentos,
  exibirDataValidade = false,
}) {
  const [documents, setDocuments] = useState();

  const { colors } = Tokens.eixods;
  const { addToast } = useToast();

  const openFile = async (idDocumento, protocolo, nomeDocumento) => {
    const response = await getDocumentosByProtocolo(idDocumento, protocolo);

    if (response?.status === 200 && response?.data) {
      const blob = new Blob([response.data], {
        type: nomeDocumento.toLowerCase().endsWith('.pdf')
          ? 'application/pdf'
          : 'image/png',
      });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } else if (response?.status === 403) {
      addToast({
        type: 'error',
        title: 'Sem permissão',
        description:
          'O usuário deve ser um operador para ter acesso aos documentos.',
      });
    } else {
      addToast({
        type: 'error',
        title: 'Erro inesperado',
        description:
          'Ocorreu  um erro ao tentar buscar o documento. Tente novamente.',
      });
    }
  };

  const updateStatusDocument = async (listIndex, docIndex, documento) => {
    try {
      if (documento.ativo) {
        await inativarDocumento(documento.idDocumento, documento.protocolo);
        addToast({
          type: 'success',
          title: 'Status alterado',
          description: `O documento ${documento.tipoDocumento} foi inativado com sucesso.`,
        });
      } else {
        await ativarDocumento(documento.idDocumento, documento.protocolo);
        addToast({
          type: 'success',
          title: 'Status alterado',
          description: `O documento ${documento.tipoDocumento} foi ativado com sucesso.`,
        });
      }
    } catch {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Falha ao alterar o status do documento.',
      });
    }

    setDocuments(prevDocumentos => {
      const newDocumentos = [...prevDocumentos];
      const documentoToUpdate =
        newDocumentos[listIndex]?.documentos?.[docIndex];
      if (documentoToUpdate) {
        documentoToUpdate.ativo = !documento.ativo;
      }

      return newDocumentos;
    });
  };

  const renderHeaderContent = descricaoCadastro => (
    <Paragraph variant="p2" fontWeight="semibold">
      {descricaoCadastro}
    </Paragraph>
  );

  const alterarDataValidade = (listIndex, docIndex, event) => {
    const dataValidadeDigitada = formatarDataDigitada(event.target.value);
    const dataValidadeMoment = moment(dataValidadeDigitada, 'DD/MM/YYYY', true);
    const dataValidadeValida =
      !dataValidadeDigitada || dataValidadeMoment.isValid();

    setDocuments(prevDocumentos => {
      const newDocumentos = [...prevDocumentos];
      const documentosDoCadastro = [...newDocumentos[listIndex].documentos];

      documentosDoCadastro[docIndex] = {
        ...documentosDoCadastro[docIndex],
        dataValidadeDigitada,
        dataValidadeValida,
        dataValidade:
          dataValidadeDigitada && dataValidadeValida
            ? dataValidadeMoment.format('YYYY-MM-DD')
            : null,
      };

      newDocumentos[listIndex] = {
        ...newDocumentos[listIndex],
        documentos: documentosDoCadastro,
      };

      return newDocumentos;
    });
  };

  const confirmarDataValidade = async (listIndex, docIndex, documento) => {
    setDocuments(prevDocumentos => {
      const newDocumentos = [...prevDocumentos];
      const documentosDoCadastro = [...newDocumentos[listIndex].documentos];
      documentosDoCadastro[docIndex] = {
        ...documentosDoCadastro[docIndex],
        salvandoDataValidade: true,
      };
      newDocumentos[listIndex] = {
        ...newDocumentos[listIndex],
        documentos: documentosDoCadastro,
      };
      return newDocumentos;
    });

    try {
      await atualizarDataValidadeDocumento(
        documento.idDocumento,
        documento.protocolo,
        documento.dataValidade
      );

      setDocuments(prevDocumentos => {
        const newDocumentos = [...prevDocumentos];
        const documentosDoCadastro = [...newDocumentos[listIndex].documentos];
        const documentoAtualizado = documentosDoCadastro[docIndex];
        documentosDoCadastro[docIndex] = {
          ...documentoAtualizado,
          dataValidadeOriginal: documentoAtualizado.dataValidade,
          salvandoDataValidade: false,
        };
        newDocumentos[listIndex] = {
          ...newDocumentos[listIndex],
          documentos: documentosDoCadastro,
        };
        return newDocumentos;
      });

      addToast({
        type: 'success',
        title: 'Validade alterada',
        description: `A data de validade do documento ${documento.tipoDocumento} foi atualizada com sucesso.`,
      });
    } catch {
      setDocuments(prevDocumentos => {
        const newDocumentos = [...prevDocumentos];
        const documentosDoCadastro = [...newDocumentos[listIndex].documentos];
        documentosDoCadastro[docIndex] = {
          ...documentosDoCadastro[docIndex],
          salvandoDataValidade: false,
        };
        newDocumentos[listIndex] = {
          ...newDocumentos[listIndex],
          documentos: documentosDoCadastro,
        };
        return newDocumentos;
      });

      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Falha ao atualizar a data de validade do documento.',
      });
    }
  };

  useEffect(() => {
    const formattedDocuments = documentos.reduce((acc, documento) => {
      const { descricaoCadastro, tipoCadastro, ...restoDocumento } = documento;

      if (!acc[descricaoCadastro]) {
        acc[descricaoCadastro] = {
          descricaoCadastro,
          tipoCadastro,
          documentos: [],
        };
      }

      restoDocumento.ativo = true;
      restoDocumento.dataValidade = formatarDataValidade(
        restoDocumento.dataValidade
      );
      restoDocumento.dataValidadeDigitada = restoDocumento.dataValidade
        ? moment(restoDocumento.dataValidade, 'YYYY-MM-DD').format('DD/MM/YYYY')
        : '';
      restoDocumento.dataValidadeValida = true;
      restoDocumento.dataValidadeOriginal = restoDocumento.dataValidade;

      acc[descricaoCadastro].documentos.push(restoDocumento);
      return acc;
    }, {});

    const resultadoArray = Object.values(formattedDocuments);
    setDocuments(resultadoArray);
  }, [documentos]);

  const renderExpandableDocuments = () => (
    <S.DocumentGroups>
      {documents?.map((d, index) => (
        <Grid
          key={[d.descricaoCadastro || 'cadastro', index].join('-')}
          container
          className="document-group"
          style={{
            overflowY: 'auto',
            padding: '0px',
            background: 'transparent',
          }}
        >
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={3}
            className="document-group-title"
          >
            <Paragraph>{renderHeaderContent(d.descricaoCadastro)}</Paragraph>
          </Grid>
          {d.documentos.map((documento, docIndex) => (
            <Grid
              key={[
                documento.idDocumento || documento.tipoDocumento,
                docIndex,
              ].join('-')}
              container
              className="document-item"
              spacing={ESpacing.y}
              style={{ width: '100%' }}
            >
              <Grid
                container
                className="document-main"
                alignItems="center"
                style={{ flexWrap: 'nowrap', width: '100%' }}
              >
                <Grid
                  item
                  className="document-switch"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: '0 0 48px',
                    paddingTop: '0.5em',
                  }}
                  spacing={ESpacing.sm}
                >
                  <Tooltip side="top" text="Ativar/inativar documento.">
                    <Switch
                      checked={documento.ativo}
                      onChange={() =>
                        updateStatusDocument(index, docIndex, documento)
                      }
                      uncheckedIcon={false}
                      checkedIcon={false}
                      handleDiameter={14}
                      height={16}
                      width={40}
                      onColor={colors.brand.primary[100]}
                      offColor={colors.brand.secondary[100]}
                      aria-label="Ativar ou inativar documento"
                    />
                  </Tooltip>
                </Grid>
                <Grid
                  item
                  className="document-name"
                  style={{ flex: '1 1 auto', minWidth: 0 }}
                >
                  <Tooltip
                    side="top"
                    text={`Enviado em ${documento.dataEnvio}`}
                  >
                    <Button
                      size="auto"
                      variant="text"
                      style={{ height: 'auto' }}
                      removeBackground
                      onClick={() =>
                        openFile(
                          documento.idDocumento,
                          documento.protocolo,
                          documento.nomeDocumento
                        )
                      }
                    >
                      {documento.tipoDocumento}
                    </Button>
                  </Tooltip>
                </Grid>
                {exibirDataValidade && (
                  <>
                    <Grid
                      item
                      className="document-validity"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: '0 1 260px',
                        minWidth: '210px',
                        gap: '8px',
                      }}
                    >
                      <span>Validade:</span>
                      <Input
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="dd/MM/yyyy"
                        status={
                          documento.dataValidadeValida ? undefined : 'error'
                        }
                        title={
                          documento.dataValidadeValida
                            ? undefined
                            : 'Informe uma data válida no formato dd/MM/yyyy.'
                        }
                        value={documento.dataValidadeDigitada}
                        onChange={event =>
                          alterarDataValidade(index, docIndex, event)
                        }
                        style={{ width: '120px' }}
                      />
                    </Grid>
                    <Grid
                      item
                      className="document-confirm"
                      style={{ flex: '0 0 88px' }}
                    >
                      {documento.dataValidadeValida &&
                        documento.dataValidade !==
                          documento.dataValidadeOriginal && (
                          <AntButton
                            type="primary"
                            size="small"
                            loading={documento.salvandoDataValidade}
                            onClick={() =>
                              confirmarDataValidade(index, docIndex, documento)
                            }
                          >
                            Confirmar
                          </AntButton>
                        )}
                    </Grid>
                  </>
                )}
              </Grid>
              <Grid container className="document-observation">
                {documento.observacao && (
                  <>
                    <b>Observação: </b>
                    {documento.observacao}
                  </>
                )}
              </Grid>
            </Grid>
          ))}
        </Grid>
      ))}
    </S.DocumentGroups>
  );

  return (
    <S.DocumentsScope>
      <Card
        width="auto"
        style={{
          padding: '0px',
          maxHeight: '100%',
          overflowY: 'auto',
          background: 'transparent',
        }}
      >
        <Grid container>
          <Grid item xs={12}>
            {renderExpandableDocuments()}
          </Grid>
        </Grid>
      </Card>
    </S.DocumentsScope>
  );
}

CardDocumentosConjunto.propTypes = {
  documentos: PropTypes.arrayOf(
    PropTypes.shape({
      descricaoCadastro: PropTypes.string,
      tipoCadastro: PropTypes.string,
      idDocumento: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      protocolo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      nomeDocumento: PropTypes.string,
      tipoDocumento: PropTypes.string,
      dataEnvio: PropTypes.string,
      dataValidade: PropTypes.string,
      observacao: PropTypes.string,
    })
  ).isRequired,
  exibirDataValidade: PropTypes.bool,
};

CardDocumentosConjunto.defaultProps = {
  exibirDataValidade: false,
};
